import Hls from 'hls.js';
import toast from 'react-hot-toast';

// Setup HLS playback with hls.js
export const setupHlsPlayback = (
  songIdOrUrl: string, 
  audioRef: React.RefObject<HTMLAudioElement>,
  setHlsInstance: (instance: Hls | null) => void,
  hlsInstance: Hls | null,
  autoPlay = true
) => {
  if (!audioRef.current) {
    return;
  }

  // Destroy any existing HLS instance
  if (hlsInstance) {
    try {
      hlsInstance.destroy();
    } catch (err) {
      console.error('Error destroying previous HLS instance:', err);
    }
    setHlsInstance(null);
  }

  // First check if we already have a full URL
  let hlsUrl = songIdOrUrl;
  let isFullUrl = songIdOrUrl.startsWith('http') || songIdOrUrl.startsWith('/api/') || songIdOrUrl.startsWith('/hls/');
  
  // If it's a song ID (not a URL), construct the correct API URL
  if (!isFullUrl) {
    const apiBaseUrl = import.meta.env.VITE_API_URL || '/api';
    // The backend /api/songs/:id/stream endpoint returns the actual HLS manifest path
    hlsUrl = `${apiBaseUrl}/songs/${songIdOrUrl}/stream`; // This endpoint will return the correct HLS or direct stream
  } else if (hlsUrl.includes('/api/songs/') && hlsUrl.includes('/hls/')) {
    // If it's a URL like /api/songs/ID/hls/playlist.m3u8, simplify it to /hls/ID/playlist.m3u8
    // This handles cases where musicPlayerApi might have added /api/songs/ prefix
    const parts = hlsUrl.split('/hls/');
    if (parts.length > 1) {
      hlsUrl = `/hls/${parts[1]}`;
    }
  }

  // Ensure the manifest name is playlist.m3u8 if it's an HLS URL
  if (hlsUrl.includes('/hls/') && hlsUrl.endsWith('/master.m3u8')) {
    hlsUrl = hlsUrl.replace('/master.m3u8', '/playlist.m3u8');
  }

  // First check for native browser HLS support
  const supportsNativeHls = audioRef.current.canPlayType('application/vnd.apple.mpegurl');
  
  if (supportsNativeHls) {
    // Always reset currentTime to 0 before setting source
    const audioElement = audioRef.current;
    audioElement.currentTime = 0;
    // Hint mobile browsers (iOS Safari) to begin playback automatically
    audioElement.autoplay = !!autoPlay;
    audioElement.src = hlsUrl;

    const handleLoadedMetadata = () => {
      // Make sure we're at the beginning
      audioElement.currentTime = 0;

      if (autoPlay) {
        const playPromise = audioElement.play();
        if (playPromise) {
          playPromise.catch(err => {
            console.error('Playback error:', err);
            toast.error(`Playback error: ${err.message}`);
          });
        }
      }
    };

    audioElement.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
  } 
  // Use HLS.js if supported
  else if (Hls.isSupported()) {
    const hlsConfig = {
      debug: false,
      enableWorker: true,
      lowLatencyMode: false, // Set to false for more stable playback
      backBufferLength: 90,
      // Add more robust error recovery
      manifestLoadingTimeOut: 20000, // Increase timeout
      manifestLoadingMaxRetry: 6, // Increase retries
      manifestLoadingRetryDelay: 500, // Start with a short delay
      manifestLoadingMaxRetryTimeout: 6000, // Maximum delay between retries
      levelLoadingTimeOut: 20000,
      fragLoadingTimeOut: 20000,
      // Improve recovery strategy
      startFragPrefetch: true,
      testBandwidth: true,
      progressive: true
    };
    
    const newHlsInstance = new Hls(hlsConfig);

    // Set up error handling before loading the source
    newHlsInstance.on(Hls.Events.ERROR, function(event, data) {
      console.warn('HLS error:', data);
      
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            // console.log('Fatal network error encountered, trying to recover');
            toast.error('Network error, trying to recover...');
            // Try to recover network error
            newHlsInstance.startLoad();
            break;
            
          case Hls.ErrorTypes.MEDIA_ERROR:
            // console.log('Fatal media error encountered, trying to recover');  
            toast.error('Media error, trying to recover...');
            // Try to recover media error
            newHlsInstance.recoverMediaError();
            break;
            
          default:
            // For any other fatal error, destroy and recreate
            // console.log('Fatal error, resetting player');
            toast.error('Fatal error, resetting player...');
            try {
              newHlsInstance.destroy();
              
              // Small delay before retrying
              setTimeout(() => {
                setupHlsPlayback(songIdOrUrl, audioRef, setHlsInstance, null, autoPlay);
              }, 1000);
            } catch (e) {
              console.error('Error during HLS instance reset:', e);
              toast.error('Playback error, please try again');
            }
            break;
        }
      } else {
        // For non-fatal errors, log and continue
        // console.log('Non-fatal HLS error:', data.type, data.details);
      }
    });

    // Pre-check that the URL is accessible before loading
    fetch(hlsUrl, { 
      method: 'HEAD',
      cache: 'no-cache'
    })
    .then(response => {
      if (!response.ok) {
        const error = new Error(`Playlist access failed (${response.status})`);
        console.error(`HLS playlist error: ${error.message} for URL ${hlsUrl}`);
        throw error;
      }
      
      // Only load source after confirming it's accessible
      console.log(`Loading HLS source: ${hlsUrl}`);
      // Hint mobile browsers to autoplay after source switch
      if (audioRef.current) {
        audioRef.current.autoplay = !!autoPlay;
      }
      newHlsInstance.loadSource(hlsUrl);
      newHlsInstance.attachMedia(audioRef.current!);
      
      // Monitor for HLS events
      newHlsInstance.on(Hls.Events.MEDIA_ATTACHED, function() {
        // console.log('HLS: Media attached');
        // Always reset currentTime to 0
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
        
        if (autoPlay) {
          audioRef.current?.play().catch(err => {
            console.error('Media attached play error:', err);
          });
        }
      });

      newHlsInstance.on(Hls.Events.MANIFEST_PARSED, function(event, data) {
        //  console.log('HLS: Manifest parsed, levels:', data.levels.length);
        // Make sure we're at the beginning
        if (audioRef.current) {
          audioRef.current.currentTime = 0;
        }
      });
      
      // Set the new HLS instance in state
      setHlsInstance(newHlsInstance);
    })
    .catch(err => {
      console.error('Error pre-checking playlist:', err);
      
      // Make sure to clean up and destroy the hls instance to prevent memory leaks
      try {
        newHlsInstance.detachMedia();
        newHlsInstance.destroy();
      } catch (destroyErr) {
        console.error('Error cleaning up HLS instance after playlist error:', destroyErr);
      }
      
      // Notify the user of the error
      if (err.message && err.message.includes('404')) {
        toast.error('Song file not found. Skipping to next song...');
        
        // Dispatch a custom event that the song couldn't be loaded
        const songErrorEvent = new CustomEvent('songStreamError', { 
          detail: { error: err.message, url: hlsUrl }
        });
        document.dispatchEvent(songErrorEvent);
      } else {
        toast.error('Error loading playlist. Please try again.');
      }
      
      // Never fall back to native HLS if there was an error - it won't work
      if (audioRef.current) {
        try {
          audioRef.current.pause();
          audioRef.current.removeAttribute('src');
          audioRef.current.load();
        } catch (audioErr) {
          console.error('Error cleaning up audio element:', audioErr);
        }
      }
    });
  } else {
    toast.error('HLS playback is not supported in this browser');
  }
};

// Setup native HLS playback for browsers that support it
export const setupNativeHlsPlayback = (
  hlsUrl: string, 
  audioRef: React.RefObject<HTMLAudioElement>,
  setAudioError: (error: string | null) => void,
  setDetailedError: (error: string | null) => void,
  hlsInstance: Hls | null,
  setHlsInstance: (instance: Hls | null) => void,
  autoPlay = true
) => {
  // Validate URL
  if (!hlsUrl) {
    return;
  }
  
  // Check for audio element
  if (!audioRef.current) {
    return;
  }
  
  try {
    // Destroy any existing HLS instance
    if (hlsInstance) {
      hlsInstance.destroy();
      setHlsInstance(null);
    }
    
    // Reset errors
    setAudioError(null);
    setDetailedError(null);
    
    // Set audio source directly
    audioRef.current.src = hlsUrl;
    
    // Set up event listeners for debugging
    const onLoadedMetadata = () => {
      if (autoPlay) {
        const playPromise = audioRef.current?.play();
        if (playPromise) {
          playPromise.catch(err => {
            toast.error(`Playback error: ${err.message}`);
          });
        }
      }
    };
    
    const onError = (e: Event) => {
      const error = audioRef.current?.error as MediaError;
      
      // Fix the type mismatch - don't set the MediaError object directly
      setAudioError("Audio playback error");
      if ((error as any)?.message) {
        setDetailedError((error as any).message);
      } else if (error) {
        // Set a more descriptive error based on the error code
        const errorMessages: Record<number, string> = {
          1: "Media resource fetch failed",
          2: "Network error during playback",
          3: "Media decoding failed",
          4: "Media source not supported"
        };
        setDetailedError(errorMessages[error.code] || `Unknown error (code ${error.code})`);
      }
      
      toast.error(`Playback error: ${(error as any)?.message || 'Unknown error'}`);
    };
    
    // Remove any existing listeners to avoid duplication
    audioRef.current.removeEventListener('loadedmetadata', onLoadedMetadata);
    audioRef.current.removeEventListener('error', onError);
    
    // Add the listeners
    audioRef.current.addEventListener('loadedmetadata', onLoadedMetadata);
    audioRef.current.addEventListener('error', onError);
  } catch (err) {
    toast.error(`Setup error: ${(err as Error).message}`);
  }
};

// Setup special playback for Chrome/Opera browsers
export const setupChromeOperaSpecialPlayback = (
  hlsUrl: string, 
  audioRef: React.RefObject<HTMLAudioElement>,
  setAudioError: (error: string | null) => void,
  setDetailedError: (error: string | null) => void,
  setupNativeHlsPlayback: (hlsUrl: string, autoPlay: boolean) => void,
  autoPlay = true
) => {
  // This is a hybrid approach that uses a direct audio element pointing to the HLS stream
  // but with additional optimizations for Chrome/Opera
  
  if (!audioRef.current) {
    return;
  }
  
  // Reset the audio element
  try {
    if (!audioRef.current.paused) {
      audioRef.current.pause();
    }
    audioRef.current.removeAttribute('src');
    audioRef.current.load();
  } catch (err) {
    // Error handling
  }
  
  // First, try to fetch the manifest directly to validate access
  fetch(hlsUrl, { 
    method: 'GET',
    headers: {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache'
    }
  })
  .then(response => {
    if (response.ok) {
      return response.text();
    } else {
      throw new Error(`Manifest fetch failed with status: ${response.status}`);
    }
  })
  .then(manifest => {
    // Extract the first segment URL if possible
    const segmentMatch = manifest.match(/segment_\d+\.ts/);
    if (segmentMatch) {
      const segmentUrl = hlsUrl.substring(0, hlsUrl.lastIndexOf('/') + 1) + segmentMatch[0];
      
      // Test fetch the first segment to ensure media is accessible
      return fetch(segmentUrl, { method: 'HEAD' })
        .then(segResponse => {
          if (segResponse.ok) {
            return true;
          } else {
            throw new Error(`First segment fetch failed with status: ${segResponse.status}`);
          }
        });
    }
    return true;
  })
  .then(() => {
    // If we got here, both manifest and segment are accessible
    
    // Set source with special query parameters for Chrome/Opera
    const chromeSpecialUrl = hlsUrl + (hlsUrl.includes('?') ? '&' : '?') + 'chrome_special=1&direct=1&_nocache=' + Date.now();
    
    // Set crossOrigin attribute to help with CORS
    audioRef.current!.crossOrigin = "anonymous";
    
    // Set the audio source
    audioRef.current!.src = chromeSpecialUrl;
    
    // Make sure autoplay attribute is set if needed
    if (autoPlay) {
      audioRef.current!.autoplay = true;
    } else {
      audioRef.current!.autoplay = false;
    }
    
    // Add special event listeners for Chrome/Opera
    const handleCanPlaySpecial = () => {
      if (autoPlay && audioRef.current) {
        // For Chrome/Opera, wait longer before trying to play
        setTimeout(() => {
          if (audioRef.current) {
            const playPromise = audioRef.current.play();
            if (playPromise) {
              playPromise
                .then(() => {})
                .catch(err => {
                  toast.error('Autoplay blocked - please click play');
                });
            }
          }
        }, 2000);
      }
    };
    
    // Add listeners for all relevant events
    const events = ['loadstart', 'loadedmetadata', 'canplay', 'canplaythrough', 'playing', 'pause', 'waiting', 'error'];
    events.forEach(event => {
      audioRef.current!.addEventListener(event, () => {
        // Audio events
      });
    });
    
    audioRef.current!.addEventListener('canplay', handleCanPlaySpecial, { once: true });
    
    // Load the audio to start buffering
    audioRef.current!.load();
    
    // Set up an error handler
    const handleErrorSpecial = (e: Event) => {
      const error = audioRef.current?.error as any;
      
      if (error) {
        setAudioError(`Error code: ${error.code}`);
        setDetailedError(error.message || 'Unknown error');
        
        toast.error('Playback failed - please try again');
      }
    };
    
    audioRef.current!.addEventListener('error', handleErrorSpecial);
  })
  .catch(err => {
    toast.error('Failed to set up playback');
    
    // As last resort, try regular native HLS
    setupNativeHlsPlayback(hlsUrl, autoPlay);
  });
}; 